import { useState, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useLogin } from "@/lib/queries"
import { useAuth } from "@/contexts/AuthContext"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { SsoButton } from "@/components/SsoButton"

export function LoginPage() {
  const [loginId, setLoginId] = useState("")
  const [password, setPassword] = useState("")
  const navigate = useNavigate()
  const login = useLogin()
  const { isAuthenticated, isLoading } = useAuth()

  const [authError, setAuthError] = useState<string | null>(() => {
    const stored = sessionStorage.getItem("auth_error")
    if (stored) sessionStorage.removeItem("auth_error")
    if (new URLSearchParams(window.location.search).get("sso_error")) {
      return "We couldn't sign you in with single sign-on. Use your email and password, or contact your administrator."
    }
    return stored
  })

  const [redirectTo] = useState(
    () => new URLSearchParams(window.location.search).get("redirect") || "/"
  )

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate({ to: redirectTo })
    }
  }, [isLoading, isAuthenticated, navigate, redirectTo])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    login.mutate(
      { login: loginId, password },
      {
        onSuccess: () => navigate({ to: redirectTo }),
      }
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col items-center text-center">
          <img src="/signature_logo.png" alt="Specia" className="h-10 mb-2" />
          <p className="text-sm text-muted-foreground">
            Sign in to your account
          </p>
        </CardHeader>
        <CardContent>
          {(authError || login.isError) && (
            <p className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive text-center">
              {login.isError
                ? (login.error instanceof Error ? login.error.message : "Invalid credentials")
                : authError}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login">Email / Username</Label>
              <Input
                id="login"
                type="text"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="Enter your email or username"
                required
                autoFocus
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-2">
            <SsoButton label="Sign in with Google" path="/auth/google_oauth2?from=customer" />
            <SsoButton label="Sign in with Microsoft" path="/auth/entra_id?from=customer" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
